import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, formatDateTime, formatMinutes, formatMoney, formatQty, minorToMajorString, moneySign, parseDurationToMinutes, parseMoneyToMinor } from '../../lib/format';
import { Badge, Chip, type Tone } from '../design/primitives/Misc';
import { Input, Select, Switch, Textarea } from '../design/primitives/Input';
import { DatePicker } from '../design/primitives/DatePicker';
import { Icon } from '../design/icons';
import type { FieldMeta, FieldType } from '../meta/types';
import { RelationLabel, RelationPicker } from './RelationPicker';
import { RichTextEditor, RichTextView } from './RichText';
import { TagsInput } from './TagsInput';
import type { WidgetComponent } from '../module';
import { ShapeEditor, ShapeView } from './ShapeEditor';

// One renderer + one editor per FieldMeta.type. Screens never switch on type themselves —
// they ask the registry. `FieldMeta.widget` names a ModuleUI widget (or a built-in one)
// that replaces both.

export interface FieldRendererProps {
  field: FieldMeta;
  value: unknown;
  row?: Record<string, unknown>;
  /** Table cell (truncate, compact) vs detail (full). */
  compact?: boolean;
}

export interface FieldEditorProps {
  field: FieldMeta;
  value: unknown;
  onChange: (value: unknown) => void;
  row?: Record<string, unknown>;
  autoFocus?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  size?: 'sm' | 'md';
  /** Inline cell editors: commit on Enter, cancel on Esc. */
  onCommit?: () => void;
  onCancel?: () => void;
  'aria-label'?: string;
}

export interface WidgetProps extends FieldRendererProps, Partial<Omit<FieldEditorProps, 'field' | 'value' | 'row'>> {
  mode: 'render' | 'edit';
}

export function currencyOf(field: FieldMeta, row?: Record<string, unknown>, fallback = 'USD'): string {
  const key = field.currencyField ?? 'currency';
  const v = row?.[key];
  return typeof v === 'string' && v ? v : fallback;
}

const Empty = () => <span className="hf-muted">—</span>;

function toneOf(field: FieldMeta, value: string): Tone {
  const opt = field.enum?.find((o) => o.value === value);
  return (opt?.tone as Tone | undefined) ?? 'muted';
}

function enumLabel(field: FieldMeta, value: unknown): string {
  const s = String(value);
  return field.enum?.find((o) => o.value === s)?.label ?? s.replace(/_/g, ' ');
}

function inlineKeys(e: React.KeyboardEvent, p: FieldEditorProps) {
  if (e.key === 'Enter' && !e.shiftKey && p.onCommit) {
    e.preventDefault();
    p.onCommit();
  } else if (e.key === 'Escape' && p.onCancel) {
    e.preventDefault();
    p.onCancel();
  }
}

// ── Renderers ─────────────────────────────────────────────────────────────
const renderers: Record<FieldType, (p: FieldRendererProps) => ReactNode> = {
  text: ({ value, field, row, compact }) => {
    if (value == null || value === '') return <Empty />;
    const s = String(value);
    if (field.linkTo && row?.id) return <Link className="hf-link" to={`${field.linkTo}${String(row.id)}`}>{s}</Link>;
    return <span className={compact ? 'hf-cell-trunc' : undefined} title={compact ? s : undefined}>{s}</span>;
  },
  textarea: ({ value, compact }) => (value == null || value === '' ? <Empty /> : <span className={compact ? 'hf-cell-trunc' : 'hf-prewrap'} title={compact ? String(value) : undefined}>{String(value)}</span>),
  richtext: ({ value, compact }) => <RichTextView html={value as string} clamp={compact} />,
  number: ({ value }) => (value == null || value === '' ? <Empty /> : <span className="hf-num">{formatQty(value as string)}</span>),
  integer: ({ value }) => (value == null || value === '' ? <Empty /> : <span className="hf-num">{Number(value).toLocaleString()}</span>),
  money: ({ value, field, row }) => {
    if (value == null || value === '') return <Empty />;
    const dto = typeof value === 'object' && value && 'minor' in (value as object) ? (value as { minor: string; currency?: string }) : null;
    const minor = dto ? dto.minor : (value as string | number);
    const currency = dto?.currency ?? currencyOf(field, row);
    const sign = field.name.includes('outstanding') || field.name.includes('balance') || field.name.includes('margin') ? moneySign(minor) : '';
    return <span className={`hf-money ${sign}`}>{formatMoney(minor, currency)}</span>;
  },
  boolean: ({ value }) =>
    value == null ? (
      <Empty />
    ) : value ? (
      <span className="hf-row" style={{ gap: 4, color: 'var(--pos)' }}>
        <Icon name="check" size={13} /> Yes
      </span>
    ) : (
      <span className="hf-muted">No</span>
    ),
  date: ({ value }) => (value ? <span className="hf-num">{formatDate(value as string)}</span> : <Empty />),
  datetime: ({ value }) => (value ? <span className="hf-num" title={String(value)}>{formatDateTime(value as string)}</span> : <Empty />),
  enum: ({ value, field }) => (value == null || value === '' ? <Empty /> : <Badge tone={toneOf(field, String(value))}>{enumLabel(field, value)}</Badge>),
  uuid: ({ value }) => (value ? <span className="hf-mono hf-muted" title={String(value)}>{String(value).slice(0, 8)}</span> : <Empty />),
  relation: ({ value, field, row }) => <RelationLabel field={field} row={row} value={value} />,
  json: ({ value, compact }) => {
    if (value == null) return <Empty />;
    const s = JSON.stringify(value, null, compact ? 0 : 2);
    return compact ? <span className="hf-mono hf-cell-trunc" title={s}>{s}</span> : <pre className="hf-mono hf-json">{s}</pre>;
  },
  tags: ({ value }) => {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    if (!arr.length) return <Empty />;
    return (
      <span className="hf-chiprow">
        {arr.map((t) => (
          <Chip key={t}>{t}</Chip>
        ))}
      </span>
    );
  },
  email: ({ value }) => (value ? <a className="hf-link" href={`mailto:${String(value)}`}>{String(value)}</a> : <Empty />),
  phone: ({ value }) => (value ? <a className="hf-link hf-num" href={`tel:${String(value)}`}>{String(value)}</a> : <Empty />),
  url: ({ value }) => {
    if (!value) return <Empty />;
    const s = String(value);
    return (
      <a className="hf-link" href={s} target="_blank" rel="noopener noreferrer">
        {s.replace(/^https?:\/\//, '')} <Icon name="external-link" size={11} />
      </a>
    );
  },
  duration: ({ value }) => (value == null || value === '' ? <Empty /> : <span className="hf-num">{formatMinutes(value as number)}</span>),
  color: ({ value }) =>
    value ? (
      <span className="hf-row" style={{ gap: 6 }}>
        <span className="hf-swatch" style={{ background: String(value) }} aria-hidden />
        <span className="hf-mono">{String(value)}</span>
      </span>
    ) : (
      <Empty />
    ),
};

// ── Editors ───────────────────────────────────────────────────────────────
function TextEditor(p: FieldEditorProps & { type?: string; mono?: boolean }) {
  return (
    <Input
      id={p.id}
      type={p.type ?? 'text'}
      value={(p.value as string | null) ?? ''}
      onChange={(e) => p.onChange(e.target.value === '' && p.field.nullable ? null : e.target.value)}
      onKeyDown={(e) => inlineKeys(e, p)}
      autoFocus={p.autoFocus}
      disabled={p.disabled}
      invalid={p.invalid}
      size={p.size}
      className={p.mono ? 'hf-mono' : undefined}
      aria-label={p['aria-label']}
      placeholder={p.field.help}
    />
  );
}

function NumberEditor(p: FieldEditorProps & { integer?: boolean }) {
  const [text, setText] = useState(p.value == null ? '' : String(p.value));
  useEffect(() => {
    setText(p.value == null ? '' : String(p.value));
  }, [p.value]);
  return (
    <Input
      id={p.id}
      inputMode={p.integer ? 'numeric' : 'decimal'}
      numeric
      value={text}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t.trim() === '') return p.onChange(null);
        const n = p.integer ? parseInt(t, 10) : Number(t);
        if (Number.isFinite(n)) p.onChange(n);
      }}
      onKeyDown={(e) => inlineKeys(e, p)}
      autoFocus={p.autoFocus}
      disabled={p.disabled}
      invalid={p.invalid}
      size={p.size}
      aria-label={p['aria-label']}
    />
  );
}

/** Money: humans type MAJOR units ("250", "1,000.50"); the value is integer minor units. */
export function MoneyEditor(p: FieldEditorProps & { currency?: string }) {
  const currency = p.currency ?? currencyOf(p.field, p.row);
  const [text, setText] = useState(() => (p.value == null || p.value === '' ? '' : minorToMajorString(p.value as number, currency)));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (focused) return;
    setText(p.value == null || p.value === '' ? '' : minorToMajorString(p.value as number, currency));
  }, [p.value, currency, focused]);
  const parsed = parseMoneyToMinor(text, currency);
  const invalid = text.trim() !== '' && parsed == null;
  return (
    <Input
      id={p.id}
      prefix={currency}
      inputMode="decimal"
      numeric
      autoComplete="off"
      placeholder="0.00"
      value={text}
      invalid={p.invalid || invalid}
      autoFocus={p.autoFocus}
      disabled={p.disabled}
      size={p.size}
      aria-label={p['aria-label']}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        setText(e.target.value);
        p.onChange(e.target.value.trim() === '' ? null : parseMoneyToMinor(e.target.value, currency));
      }}
      onBlur={() => {
        setFocused(false);
        if (parsed != null) setText(minorToMajorString(parsed, currency));
      }}
      onKeyDown={(e) => inlineKeys(e, p)}
    />
  );
}

/** Duration: "1:30", "90m", "1.5h" → minutes. */
export function DurationEditor(p: FieldEditorProps) {
  const [text, setText] = useState(() => (p.value == null || p.value === '' ? '' : toHm(Number(p.value))));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(p.value == null || p.value === '' ? '' : toHm(Number(p.value)));
  }, [p.value, focused]);
  const mins = parseDurationToMinutes(text);
  const invalid = text.trim() !== '' && mins == null;
  return (
    <Input
      id={p.id}
      value={text}
      placeholder="h:mm"
      numeric
      suffix={mins != null ? formatMinutes(mins) : 'h:mm'}
      invalid={p.invalid || invalid}
      autoFocus={p.autoFocus}
      disabled={p.disabled}
      size={p.size}
      aria-label={p['aria-label']}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (mins != null) setText(toHm(mins));
      }}
      onChange={(e) => {
        setText(e.target.value);
        p.onChange(e.target.value.trim() === '' ? null : parseDurationToMinutes(e.target.value));
      }}
      onKeyDown={(e) => inlineKeys(e, p)}
    />
  );
}

function toHm(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function EnumEditor(p: FieldEditorProps) {
  return (
    <Select
      id={p.id}
      value={(p.value as string | null) ?? ''}
      onChange={(e) => p.onChange(e.target.value === '' ? null : e.target.value)}
      onKeyDown={(e) => inlineKeys(e, p)}
      autoFocus={p.autoFocus}
      disabled={p.disabled}
      invalid={p.invalid}
      size={p.size}
      placeholder={p.field.required ? 'Select…' : '—'}
      options={(p.field.enum ?? []).map((o) => ({ value: o.value, label: o.label }))}
      aria-label={p['aria-label']}
    />
  );
}

function JsonEditor(p: FieldEditorProps) {
  const [text, setText] = useState(() => (p.value == null ? '' : JSON.stringify(p.value, null, 2)));
  const [bad, setBad] = useState(false);
  return (
    <Textarea
      id={p.id}
      className="hf-mono"
      value={text}
      invalid={p.invalid || bad}
      autoFocus={p.autoFocus}
      disabled={p.disabled}
      rows={6}
      aria-label={p['aria-label']}
      onChange={(e) => {
        setText(e.target.value);
        if (!e.target.value.trim()) {
          setBad(false);
          p.onChange(null);
          return;
        }
        try {
          p.onChange(JSON.parse(e.target.value));
          setBad(false);
        } catch {
          setBad(true);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') p.onCancel?.();
      }}
    />
  );
}

function ColorEditor(p: FieldEditorProps) {
  const v = (p.value as string | null) ?? '';
  return (
    <div className="hf-row" style={{ gap: 6 }}>
      <input type="color" aria-label={`${p.field.label} swatch`} value={/^#[0-9a-f]{6}$/i.test(v) ? v : '#000000'} onChange={(e) => p.onChange(e.target.value)} disabled={p.disabled} className="hf-color" />
      <Input id={p.id} className="hf-mono" value={v} placeholder="#b8481f" onChange={(e) => p.onChange(e.target.value || null)} onKeyDown={(e) => inlineKeys(e, p)} autoFocus={p.autoFocus} disabled={p.disabled} invalid={p.invalid} size={p.size} aria-label={p['aria-label']} />
    </div>
  );
}

const editors: Record<FieldType, ComponentType<FieldEditorProps>> = {
  text: (p) => <TextEditor {...p} />,
  textarea: (p) => (
    <Textarea id={p.id} value={(p.value as string | null) ?? ''} onChange={(e) => p.onChange(e.target.value === '' && p.field.nullable ? null : e.target.value)} autoFocus={p.autoFocus} disabled={p.disabled} invalid={p.invalid} rows={3} aria-label={p['aria-label']} placeholder={p.field.help} onKeyDown={(e) => { if (e.key === 'Escape') p.onCancel?.(); if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) p.onCommit?.(); }} />
  ),
  richtext: (p) => <RichTextEditor id={p.id} value={p.value as string} onChange={(html) => p.onChange(html || (p.field.nullable ? null : ''))} disabled={p.disabled} autoFocus={p.autoFocus} placeholder={p.field.help} aria-label={p['aria-label']} />,
  number: (p) => <NumberEditor {...p} />,
  integer: (p) => <NumberEditor {...p} integer />,
  money: (p) => <MoneyEditor {...p} />,
  boolean: (p) => <Switch id={p.id} checked={Boolean(p.value)} onChange={(e) => p.onChange(e.target.checked)} disabled={p.disabled} autoFocus={p.autoFocus} aria-label={p['aria-label']} label={p.size === 'sm' ? undefined : p.value ? 'Yes' : 'No'} />,
  date: (p) => <DatePicker id={p.id} value={p.value as string | null} onChange={(iso) => p.onChange(iso)} disabled={p.disabled} invalid={p.invalid} size={p.size} autoFocus={p.autoFocus} aria-label={p['aria-label']} />,
  datetime: (p) => (
    <Input
      id={p.id}
      type="datetime-local"
      value={p.value ? toLocalInput(String(p.value)) : ''}
      onChange={(e) => p.onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
      onKeyDown={(e) => inlineKeys(e, p)}
      autoFocus={p.autoFocus}
      disabled={p.disabled}
      invalid={p.invalid}
      size={p.size}
      aria-label={p['aria-label']}
    />
  ),
  enum: (p) => <EnumEditor {...p} />,
  uuid: (p) => <TextEditor {...p} mono />,
  relation: (p) => <RelationPicker id={p.id} field={p.field} row={p.row} value={p.value as string | null} onChange={(v) => p.onChange(v)} disabled={p.disabled} invalid={p.invalid} autoFocus={p.autoFocus} size={p.size} aria-label={p['aria-label']} />,
  json: (p) => <JsonEditor {...p} />,
  tags: (p) => <TagsInput id={p.id} value={p.value as string[] | null} onChange={(t) => p.onChange(t)} disabled={p.disabled} aria-label={p['aria-label']} />,
  email: (p) => <TextEditor {...p} type="email" />,
  phone: (p) => <TextEditor {...p} type="tel" />,
  url: (p) => <TextEditor {...p} type="url" />,
  duration: (p) => <DurationEditor {...p} />,
  color: (p) => <ColorEditor {...p} />,
};

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Registry API ──────────────────────────────────────────────────────────
export type Widgets = Record<string, WidgetComponent>;

/** Built-in widgets addressable by `FieldMeta.widget`. */
export const BUILTIN_WIDGETS: Widgets = {
  'money-dto': ({ mode, ...p }: WidgetProps) => (mode === 'edit' ? <MoneyEditor {...(p as FieldEditorProps)} /> : renderers.money(p)),
  'enum-chips': ({ mode, ...p }: WidgetProps) =>
    mode === 'edit' ? (
      <div className="hf-chiprow" role="radiogroup" aria-label={p.field.label}>
        {(p.field.enum ?? []).map((o) => (
          <Chip key={o.value} role="radio" aria-selected={p.value === o.value} active={p.value === o.value} onClick={() => p.onChange?.(o.value)} disabled={p.disabled}>
            {o.label}
          </Chip>
        ))}
      </div>
    ) : (
      renderers.enum(p)
    ),
};

export function renderField(field: FieldMeta, value: unknown, row?: Record<string, unknown>, opts: { compact?: boolean; widgets?: Widgets } = {}): ReactNode {
  const w = field.widget ? (opts.widgets?.[field.widget] ?? BUILTIN_WIDGETS[field.widget]) : undefined;
  if (w) {
    const W = w;
    return <W mode="render" field={field} value={value} row={row} compact={opts.compact} />;
  }
  if (field.type === 'json' && field.shape) return <ShapeView field={field} value={value} shape={field.shape} compact={opts.compact} widgets={opts.widgets} />;
  const R = renderers[field.type] ?? renderers.text;
  return R({ field, value, row, compact: opts.compact });
}

export function FieldEditor(props: FieldEditorProps & { widgets?: Widgets }) {
  const { widgets, ...p } = props;
  const w = p.field.widget ? (widgets?.[p.field.widget] ?? BUILTIN_WIDGETS[p.field.widget]) : undefined;
  if (w) {
    const W = w;
    return <W mode="edit" {...p} />;
  }
  if (p.field.type === 'json' && p.field.shape) return <ShapeEditor {...p} shape={p.field.shape} widgets={widgets} />;
  const E = editors[p.field.type] ?? editors.text;
  return <E {...p} />;
}

export function FieldValue(props: FieldRendererProps & { widgets?: Widgets }) {
  return <>{renderField(props.field, props.value, props.row, { compact: props.compact, widgets: props.widgets })}</>;
}

export const registry = { renderers, editors };
